import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, CardBody, MainLayout, Spinner, TextInput } from '../components';
import { useAppNavigation } from '../contexts/AppNavigationContext';
import { useAuth } from '../contexts/AuthContext';
import { LOCALE_REGISTRY, resolveSupportedLocale } from '../i18n';
import { brandClasses } from '../theme';
import { UserDto, usersAPI } from '../services/api';

const getDisplayValue = (value: string | undefined, fallback: string) => {
  return value && value.trim() ? value : fallback;
};

const extractApiErrorMessage = (error: unknown, fallback: string) => {
  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const response = 'response' in error && error.response && typeof error.response === 'object'
    ? error.response
    : null;

  const payload = response && 'data' in response && response.data && typeof response.data === 'object'
    ? response.data
    : null;

  if (payload) {
    if ('detail' in payload && typeof payload.detail === 'string' && payload.detail.trim()) {
      return payload.detail;
    }

    if ('error' in payload && typeof payload.error === 'string' && payload.error.trim()) {
      return payload.error;
    }

    if ('message' in payload && typeof payload.message === 'string' && payload.message.trim()) {
      return payload.message;
    }
  }

  return fallback;
};

const normalizeSettingsProfileResponse = (payload: unknown): UserDto | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const candidate = 'data' in payload && payload.data && typeof payload.data === 'object'
    ? payload.data
    : payload;

  if (!candidate || typeof candidate !== 'object') {
    return null;
  }

  const user = candidate as Partial<UserDto>;
  if (!user.id || !user.email) {
    return null;
  }

  return {
    id: user.id,
    email: user.email,
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    phone: user.phone,
    lastOpenedTreeId: user.lastOpenedTreeId,
    systemRole: user.systemRole,
    preferredLocale: user.preferredLocale,
  };
};

interface SettingsFieldRowProps {
  label: string;
  value: string;
}

const SettingsFieldRow: React.FC<SettingsFieldRowProps> = ({ label, value }) => (
  <div className="space-y-1">
    <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${brandClasses.subtleText}`}>{label}</p>
    <p className={`text-sm ${brandClasses.primaryText}`}>{value}</p>
  </div>
);

export const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, updateCurrentUser } = useAuth();
  const { clearNavigationState } = useAppNavigation();
  const [profile, setProfile] = useState<UserDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const canManageAdminPrivacy = user?.systemRole === 'Administrator';

  const fetchProfileSnapshot = useCallback(async () => {
    const response = await usersAPI.getProfile();
    const normalizedProfile = normalizeSettingsProfileResponse(response.data);
    if (!normalizedProfile) {
      throw new Error('Invalid profile response');
    }

    return normalizedProfile;
  }, []);

  const loadProfile = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      setSuccessMessage('');
      const nextProfile = await fetchProfileSnapshot();
      setProfile(nextProfile);
      setProfileForm({
        firstName: nextProfile.firstName ?? '',
        lastName: nextProfile.lastName ?? '',
        phone: nextProfile.phone ?? '',
      });
    } catch (err) {
      setError(t('messages.loadFailed', { ns: 'settings' }));
    } finally {
      setIsLoading(false);
    }
  }, [fetchProfileSnapshot, t]);

  useEffect(() => {
    clearNavigationState();
  }, [clearNavigationState]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleProfileFormChange = (field: 'firstName' | 'lastName' | 'phone', value: string) => {
    setProfileForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  };

  const handlePasswordFormChange = (
    field: 'currentPassword' | 'newPassword' | 'confirmPassword',
    value: string
  ) => {
    setPasswordForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  };

  const handleStartProfileEdit = () => {
    setSuccessMessage('');
    setError('');
    setProfileForm({
      firstName: profile?.firstName ?? '',
      lastName: profile?.lastName ?? '',
      phone: profile?.phone ?? '',
    });
    setIsEditingProfile(true);
  };

  const handleCancelProfileEdit = () => {
    setProfileForm({
      firstName: profile?.firstName ?? '',
      lastName: profile?.lastName ?? '',
      phone: profile?.phone ?? '',
    });
    setError('');
    setSuccessMessage('');
    setIsEditingProfile(false);
  };

  const resetPasswordForm = () => {
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  };

  const handleStartPasswordEdit = () => {
    setError('');
    setSuccessMessage('');
    resetPasswordForm();
    setIsEditingPassword(true);
  };

  const handleCancelPasswordEdit = () => {
    resetPasswordForm();
    setError('');
    setSuccessMessage('');
    setIsEditingPassword(false);
  };

  const handleSaveProfile = useCallback(async () => {
    if (!profile) {
      return;
    }

    try {
      setIsSavingProfile(true);
      setError('');
      setSuccessMessage('');

      const updates = {
        firstName: profileForm.firstName.trim(),
        lastName: profileForm.lastName.trim(),
        phone: profileForm.phone.trim() || undefined,
      };

      await usersAPI.updateProfile(updates);

      let updatedProfile: UserDto;
      try {
        updatedProfile = await fetchProfileSnapshot();
      } catch (refreshError) {
        updatedProfile = {
          ...profile,
          ...updates,
        };
      }

      setProfile(updatedProfile);
      setProfileForm({
        firstName: updatedProfile.firstName ?? '',
        lastName: updatedProfile.lastName ?? '',
        phone: updatedProfile.phone ?? '',
      });
      updateCurrentUser({
        firstName: updatedProfile.firstName,
        lastName: updatedProfile.lastName,
        phone: updatedProfile.phone,
      });
      setSuccessMessage(t('messages.profileUpdated', { ns: 'settings' }));
      setIsEditingProfile(false);
    } catch (err) {
      setError(extractApiErrorMessage(err, t('errors.updateProfileFailed', { ns: 'settings' })));
    } finally {
      setIsSavingProfile(false);
    }
  }, [fetchProfileSnapshot, profile, profileForm.firstName, profileForm.lastName, profileForm.phone, t, updateCurrentUser]);

  const handleSavePassword = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setError(t('errors.completePasswordFields', { ns: 'settings' }));
      setSuccessMessage('');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError(t('errors.passwordsDoNotMatch', { ns: 'settings' }));
      setSuccessMessage('');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setError(t('errors.passwordTooShort', { ns: 'settings' }));
      setSuccessMessage('');
      return;
    }

    try {
      setIsSavingPassword(true);
      setError('');
      setSuccessMessage('');

      await usersAPI.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });

      resetPasswordForm();
      setIsEditingPassword(false);
      setSuccessMessage(t('messages.passwordUpdated', { ns: 'settings' }));
    } catch (err) {
      setError(extractApiErrorMessage(err, t('errors.updatePasswordFailed', { ns: 'settings' })));
    } finally {
      setIsSavingPassword(false);
    }
  };

  const displayProfile = {
    firstName: getDisplayValue(profile?.firstName, t('fallbacks.notProvided', { ns: 'settings' })),
    lastName: getDisplayValue(profile?.lastName, t('fallbacks.notProvided', { ns: 'settings' })),
    email: getDisplayValue(profile?.email, t('fallbacks.notAvailable', { ns: 'settings' })),
    phone: getDisplayValue(profile?.phone, t('fallbacks.notProvided', { ns: 'settings' })),
    systemRole: getDisplayValue(profile?.systemRole, t('fallbacks.standardUser', { ns: 'settings' })),
    lastOpenedTreeId: getDisplayValue(profile?.lastOpenedTreeId, t('fallbacks.noRecentTree', { ns: 'settings' })),
    preferredLocale: profile?.preferredLocale
      ? LOCALE_REGISTRY[resolveSupportedLocale(profile.preferredLocale)].nativeName
      : t('fallbacks.defaultLanguage', { ns: 'settings' }),
    id: getDisplayValue(profile?.id, t('fallbacks.notAvailable', { ns: 'settings' })),
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <section className={brandClasses.heroCard}>
          <h1 className={`text-3xl font-bold ${brandClasses.titleText}`}>{t('title', { ns: 'settings' })}</h1>
          <p className={`mt-2 text-sm ${brandClasses.mutedText}`}>
            {t('subtitle', { ns: 'settings' })}
          </p>
        </section>

        {error && (
          <div className="space-y-3">
            <Alert variant="error" message={error} />
            {!profile && (
              <Button type="button" variant="outline" onClick={loadProfile}>
                {t('buttons.retry', { ns: 'common' })}
              </Button>
            )}
          </div>
        )}

        {successMessage && (
          <div className="space-y-3">
            <Alert variant="success" message={successMessage} />
          </div>
        )}

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <Spinner size="lg" className="mb-4" />
            <p className={`text-lg ${brandClasses.mutedText}`}>{t('status.loadingSettings', { ns: 'settings' })}</p>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-3">
            <Card className={`${brandClasses.elevatedCard} lg:col-span-2`}>
              <CardBody className="space-y-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className={`text-xl font-semibold ${brandClasses.primaryText}`}>{t('sections.profileTitle', { ns: 'settings' })}</h2>
                    <p className={`mt-1 text-sm ${brandClasses.mutedText}`}>{t('sections.profileSubtitle', { ns: 'settings' })}</p>
                  </div>

                  {!isEditingProfile ? (
                    <Button type="button" variant="outline" onClick={handleStartProfileEdit}>
                      {t('buttons.editProfile', { ns: 'common' })}
                    </Button>
                  ) : (
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" onClick={handleCancelProfileEdit} disabled={isSavingProfile}>
                        {t('buttons.cancel', { ns: 'common' })}
                      </Button>
                      <Button type="button" onClick={handleSaveProfile} isLoading={isSavingProfile}>
                        {t('buttons.saveChanges', { ns: 'common' })}
                      </Button>
                    </div>
                  )}
                </div>

                {!isEditingProfile ? (
                  <div className="grid gap-6 sm:grid-cols-2">
                    <SettingsFieldRow label={t('fields.firstName', { ns: 'settings' })} value={displayProfile.firstName} />
                    <SettingsFieldRow label={t('fields.lastName', { ns: 'settings' })} value={displayProfile.lastName} />
                    <SettingsFieldRow label={t('fields.email', { ns: 'settings' })} value={displayProfile.email} />
                    <SettingsFieldRow label={t('fields.phone', { ns: 'settings' })} value={displayProfile.phone} />
                  </div>
                ) : (
                  <div className="grid gap-6 sm:grid-cols-2">
                    <TextInput
                      id="settings-first-name"
                      label={t('fields.firstName', { ns: 'settings' })}
                      value={profileForm.firstName}
                      onChange={(event) => handleProfileFormChange('firstName', event.target.value)}
                    />
                    <TextInput
                      id="settings-last-name"
                      label={t('fields.lastName', { ns: 'settings' })}
                      value={profileForm.lastName}
                      onChange={(event) => handleProfileFormChange('lastName', event.target.value)}
                    />
                    <div className="space-y-1">
                      <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${brandClasses.subtleText}`}>{t('fields.email', { ns: 'settings' })}</p>
                      <p className={`text-sm ${brandClasses.primaryText}`}>{displayProfile.email}</p>
                    </div>
                    <TextInput
                      id="settings-phone"
                      label={t('fields.phone', { ns: 'settings' })}
                      value={profileForm.phone}
                      onChange={(event) => handleProfileFormChange('phone', event.target.value)}
                    />
                  </div>
                )}

              </CardBody>
            </Card>

            <Card className={brandClasses.elevatedCard}>
              <CardBody className="space-y-6">
                <div>
                  <h2 className={`text-xl font-semibold ${brandClasses.primaryText}`}>{t('sections.accountTitle', { ns: 'settings' })}</h2>
                  <p className={`mt-1 text-sm ${brandClasses.mutedText}`}>{t('sections.accountSubtitle', { ns: 'settings' })}</p>
                </div>

                <div className="space-y-5">
                  <SettingsFieldRow label={t('fields.systemRole', { ns: 'settings' })} value={displayProfile.systemRole} />
                  <SettingsFieldRow label={t('fields.preferredLanguage', { ns: 'settings' })} value={displayProfile.preferredLocale} />
                  <SettingsFieldRow label={t('fields.lastOpenedTree', { ns: 'settings' })} value={displayProfile.lastOpenedTreeId} />
                  <SettingsFieldRow label={t('fields.accountId', { ns: 'settings' })} value={displayProfile.id} />
                </div>
              </CardBody>
            </Card>

            <Card className={`${brandClasses.elevatedCard} lg:col-span-3`}>
              <CardBody className="space-y-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className={`text-xl font-semibold ${brandClasses.primaryText}`}>{t('sections.securityTitle', { ns: 'settings' })}</h2>
                    <p className={`mt-1 text-sm ${brandClasses.mutedText}`}>{t('sections.securitySubtitle', { ns: 'settings' })}</p>
                  </div>

                  {!isEditingPassword ? (
                    <Button type="button" variant="outline" onClick={handleStartPasswordEdit}>
                      {t('buttons.changePassword', { ns: 'common' })}
                    </Button>
                  ) : (
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" onClick={handleCancelPasswordEdit} disabled={isSavingPassword}>
                        {t('buttons.cancel', { ns: 'common' })}
                      </Button>
                      <Button type="button" onClick={handleSavePassword} isLoading={isSavingPassword}>
                        {t('buttons.updatePassword', { ns: 'common' })}
                      </Button>
                    </div>
                  )}
                </div>

                {!isEditingPassword ? (
                  <div className="grid gap-6 md:grid-cols-2">
                    <SettingsFieldRow label={t('fields.passwordManagement', { ns: 'settings' })} value={t('security.passwordManagementValue', { ns: 'settings' })} />
                    <SettingsFieldRow label={t('fields.accountActions', { ns: 'settings' })} value={t('security.accountActionsValue', { ns: 'settings' })} />
                  </div>
                ) : (
                  <div className="grid gap-6 md:grid-cols-3">
                    <TextInput
                      id="settings-current-password"
                      label={t('fields.currentPassword', { ns: 'settings' })}
                      type="password"
                      value={passwordForm.currentPassword}
                      onChange={(event) => handlePasswordFormChange('currentPassword', event.target.value)}
                      autoComplete="current-password"
                    />
                    <TextInput
                      id="settings-new-password"
                      label={t('fields.newPassword', { ns: 'settings' })}
                      type="password"
                      value={passwordForm.newPassword}
                      onChange={(event) => handlePasswordFormChange('newPassword', event.target.value)}
                      autoComplete="new-password"
                    />
                    <TextInput
                      id="settings-confirm-password"
                      label={t('fields.confirmPassword', { ns: 'settings' })}
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(event) => handlePasswordFormChange('confirmPassword', event.target.value)}
                      autoComplete="new-password"
                    />
                  </div>
                )}

              </CardBody>
            </Card>

            {canManageAdminPrivacy ? (
              <Card className={`${brandClasses.elevatedCard} lg:col-span-3`}>
                <CardBody className="space-y-6">
                  <div>
                    <h2 className={`text-xl font-semibold ${brandClasses.primaryText}`}>{t('sections.adminToolsTitle', { ns: 'settings' })}</h2>
                    <p className={`mt-1 text-sm ${brandClasses.mutedText}`}>{t('sections.adminToolsSubtitle', { ns: 'settings' })}</p>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <Button type="button" variant="outline" onClick={() => navigate('/admin#system-settings')}>
                      {t('buttons.systemSetting', { ns: 'settings' })}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => navigate('/admin#user-management')}>
                      {t('buttons.userManagement', { ns: 'settings' })}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            ) : null}
          </div>
        )}
      </div>
    </MainLayout>
  );
};
